// ═══════════════════════════════════════════════════════════════════════════
// Sincronizador automático — Etapa 4b
//
// El operador: "mejor guardar local e intentar actualizar cuando haya
// conexión". Aquí no hay botón ni decisión: la subida ocurre sola.
//
// CUÁNDO INTENTA
//   · al arrancar la app
//   · cuando el navegador avisa que volvió la conexión
//   · unos segundos después de cada cambio
//   · cada pocos minutos, por si acaso
//
// Nada de esto bloquea la interfaz. Si falla, se reintenta; si algo es
// irrecuperable, se aparta y se muestra en Config sin estorbar al resto.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";
import { subir, resumenBitacora } from "./bitacora.js";

const ESPERA_TRAS_CAMBIO = 4000;    // agrupa ráfagas de ediciones
const INTERVALO_REPASO   = 120000;  // 2 min

let temporizador = null;
let repaso = null;
let corriendo = false;
const oyentes = new Set();

/** Aplica una mutación contra Postgres. Lanza si falla. */
async function aplicar(m) {
  // Remisión: función atómica que asigna folio, inserta líneas y descuenta
  // inventario. Idempotente por legacy_id, así que reenviarla es seguro.
  if (m.operacion === "crear_remision") {
    const { data, error } = await supabase.rpc("crear_remision", m.datos);
    if (error) throw error;
    // El folio recién asignado se comunica para que la app lo muestre.
    avisarFolio({ localId: m.datos.p_local_id, folio: data?.folio, datos: data });
    return;
  }

  if (m.operacion === "cancelar_remision") {
    const { error } = await supabase.rpc("cancelar_remision", m.datos);
    if (error) throw error;
    return;
  }

  if (m.operacion === "registrar_pago") {
    const { error } = await supabase.rpc("registrar_pago", m.datos);
    if (error) throw error;
    return;
  }

  // Cliente: no es el upsert de una fila. Lleva la lista de precios (FK), los
  // precios especiales (tabla aparte) y el modo de IEPS. Se resuelve contra la
  // base al aplicar, que puede ser días después de haberse encolado.
  if (m.operacion === "guardar_cliente") {
    const { error } = await supabase.rpc("guardar_cliente", m.datos);
    if (error) throw error;
    return;
  }

  if (m.operacion === "upsert") {
    // onConflict por (tenant_id, legacy_id): reenviar la misma mutación
    // actualiza en vez de duplicar. Esa es la idempotencia del lado servidor,
    // gemela del uuid del lado cliente.
    const { error } = await supabase
      .from(m.tabla)
      .upsert(m.datos, { onConflict: "tenant_id,legacy_id" });
    if (error) throw error;
    return;
  }

  if (m.operacion === "delete") {
    // Borrado LÓGICO: un delete físico no se puede sincronizar a un
    // dispositivo que estaba sin conexión.
    const { error } = await supabase
      .from(m.tabla)
      .update({ deleted_at: new Date().toISOString() })
      .eq("tenant_id", m.datos.tenant_id)
      .eq("legacy_id", m.datos.legacy_id);
    if (error) throw error;
    return;
  }

  throw new Error(`Operación desconocida: ${m.operacion}`);
}

/** Intenta subir ahora. Nunca lanza. */
export async function sincronizar() {
  if (corriendo) return null;
  corriendo = true;
  try {
    const r = await subir(aplicar);
    avisar();
    return r;
  } catch (e) {
    console.warn("[sync] error inesperado:", e?.message);
    return null;
  } finally {
    corriendo = false;
  }
}

/** Programa una subida tras un cambio, agrupando ráfagas. */
export function programarSincronizacion() {
  if (temporizador) clearTimeout(temporizador);
  temporizador = setTimeout(() => { temporizador = null; sincronizar(); }, ESPERA_TRAS_CAMBIO);
}

/** Arranca los disparadores automáticos. Devuelve función para detenerlos. */
export function iniciarSincronizacion() {
  const alVolverLaRed = () => sincronizar();
  window.addEventListener("online", alVolverLaRed);
  repaso = setInterval(sincronizar, INTERVALO_REPASO);
  sincronizar();   // al arrancar

  return () => {
    window.removeEventListener("online", alVolverLaRed);
    if (repaso) clearInterval(repaso);
    if (temporizador) clearTimeout(temporizador);
  };
}

// ── Para que la interfaz pueda mostrar el estado ───────────────────────────

// Quien quiera enterarse de los folios asignados al sincronizar.
const oyentesFolio = new Set();
export function alAsignarFolio(fn) {
  oyentesFolio.add(fn);
  return () => oyentesFolio.delete(fn);
}
function avisarFolio(info) {
  for (const fn of oyentesFolio) { try { fn(info); } catch { /* un oyente roto no rompe el resto */ } }
}

export function alCambiarBitacora(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function avisar() {
  const r = resumenBitacora();
  for (const fn of oyentes) { try { fn(r); } catch { /* un oyente roto no rompe el resto */ } }
}

export { resumenBitacora };
